import {
  AMBIENTS,
  BACKUP_APP_ID,
  CLAMPS,
  DEFAULT_SETTINGS,
  LAYOUTS,
  MAX_BACKUP_BYTES,
  MAX_LOG_PER_DAY,
  MAX_SIDE_TIMERS,
  MINUTE,
  SOUNDS,
  TEXT_SCALE_STEP,
  TYPEFACES,
} from './constants';
import { compact } from './history';
import { capEndAt, idleRun, phaseMs } from './timer';
import type {
  AppData,
  DayRecord,
  Phase,
  RunState,
  SessionEntry,
  Settings,
  SideTimer,
  StreakCarry,
  Task,
} from './types';
import { bool, dayKey, int, isDayKey, isObj, num, oneOf, str, uid } from './util';

const PHASES: readonly Phase[] = ['focus', 'short', 'long'];

/** Ids end up in element ids and selectors, so only plain characters are accepted. */
const ID_RE = /^[\w-]{1,40}$/;
export const safeId = (v: unknown): string => (typeof v === 'string' && ID_RE.test(v) ? v : uid());

export function sanitizeSettings(raw: unknown, base: Settings = DEFAULT_SETTINGS): Settings {
  const s = isObj(raw) ? raw : {};
  const n = (k: keyof typeof CLAMPS & keyof Settings) =>
    int(s[k], base[k] as number, CLAMPS[k][0], CLAMPS[k][1]);
  return {
    focus: n('focus'),
    short: n('short'),
    long: n('long'),
    interval: n('interval'),
    autoBreak: bool(s.autoBreak, base.autoBreak),
    autoFocus: bool(s.autoFocus, base.autoFocus),
    sound: oneOf(s.sound, SOUNDS, base.sound),
    volume: n('volume'),
    ticking: bool(s.ticking, base.ticking),
    theme: oneOf(s.theme, ['light', 'dark'] as const, base.theme),
    dailyGoal: n('dailyGoal'),
    ambient: oneOf(s.ambient, AMBIENTS, base.ambient),
    ambientVolume: n('ambientVolume'),
    wakeLock: bool(s.wakeLock, base.wakeLock),
    layout: oneOf(s.layout, LAYOUTS, base.layout),
    typeface: oneOf(s.typeface, TYPEFACES, base.typeface),
    // snapped to the slider's steps so a hand-edited 103 still lands on a real stop
    textScale: snapScale(n('textScale')),
  };
}

export const snapScale = (v: number): number =>
  Math.min(
    CLAMPS.textScale[1],
    Math.max(CLAMPS.textScale[0], Math.round(v / TEXT_SCALE_STEP) * TEXT_SCALE_STEP),
  );

function sanitizeRun(raw: unknown, settings: Settings, now: number): RunState {
  if (!isObj(raw)) return idleRun(settings);
  const phase = oneOf(raw.phase, PHASES, 'focus');
  const round = int(raw.round, 1, 1, settings.interval);
  const total = int(raw.total, phaseMs(settings, phase), 1000, 24 * 60 * MINUTE);
  const running = bool(raw.running, false) && num(raw.endAt, 0) > 0;
  const remaining = int(raw.remaining, total, 0, total);
  return capEndAt(
    {
      phase,
      round,
      total,
      remaining,
      running,
      endAt: running ? num(raw.endAt, 0) : 0,
      interruptions: int(raw.interruptions, 0, 0, 999),
    },
    now,
  );
}

function sanitizeTask(raw: unknown, now: number): Task | null {
  if (!isObj(raw)) return null;
  const text = str(raw.text, '', 200).trim();
  if (!text) return null;
  const est = int(raw.est, 1, CLAMPS.est[0], CLAMPS.est[1]);
  return {
    id: safeId(raw.id),
    text,
    notes: str(raw.notes, '', 2000),
    est,
    count: int(raw.count, 0, 0, 999),
    done: bool(raw.done, false),
    createdAt: num(raw.createdAt, now),
  };
}

function sanitizeSide(raw: unknown, now: number): SideTimer | null {
  if (!isObj(raw)) return null;
  const total = int(raw.total, 0, 0, 24 * 60 * MINUTE);
  if (total <= 0) return null;
  const running = bool(raw.running, false);
  return capEndAt(
    {
      id: safeId(raw.id),
      name: str(raw.name, 'T1', 4),
      total,
      remaining: int(raw.remaining, total, 0, total),
      endAt: running ? num(raw.endAt, 0) : 0,
      running,
      done: bool(raw.done, false),
    },
    now,
  );
}

function sanitizeEntry(raw: unknown): SessionEntry | null {
  if (!isObj(raw)) return null;
  const ts = num(raw.ts, NaN);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  return {
    ts,
    min: int(raw.min, 25, 0, 24 * 60),
    task: typeof raw.task === 'string' ? raw.task.slice(0, 200) : null,
    interruptions: int(raw.interruptions, 0, 0, 999),
  };
}

function sanitizeDay(raw: unknown): DayRecord | null {
  if (!isObj(raw)) return null;
  const log = Array.isArray(raw.log)
    ? raw.log
        .map(sanitizeEntry)
        .filter((e): e is SessionEntry => !!e)
        .slice(0, MAX_LOG_PER_DAY)
    : [];
  return {
    sessions: int(raw.sessions, log.length, 0, 10_000),
    minutes: int(raw.minutes, 0, 0, 1_000_000),
    interruptions: int(raw.interruptions, 0, 0, 100_000),
    log,
  };
}

function sanitizeCarry(raw: unknown): StreakCarry | null {
  if (!isObj(raw) || !isDayKey(raw.lastDay)) return null;
  const streak = int(raw.streak, 0, 0, 100_000);
  return streak > 0 ? { lastDay: raw.lastDay, streak } : null;
}

export function emptyData(settings: Partial<Settings> = {}): AppData {
  const s = { ...DEFAULT_SETTINGS, ...settings };
  return {
    v: 2,
    settings: s,
    run: idleRun(s),
    tasks: [],
    activeTaskId: null,
    side: [],
    history: {},
    totals: { sessions: 0, minutes: 0 },
    streakCarry: null,
  };
}

/** Coerce arbitrary (possibly corrupted or hand-edited) v2 data into a valid AppData. */
export function sanitize(raw: unknown, now: number, base: Settings = DEFAULT_SETTINGS): AppData {
  const d = isObj(raw) ? raw : {};
  const settings = sanitizeSettings(d.settings, base);
  const tasks = (Array.isArray(d.tasks) ? d.tasks : [])
    .map((t) => sanitizeTask(t, now))
    .filter((t): t is Task => !!t)
    .slice(0, 500);
  const ids = new Set<string>();
  for (const t of tasks) {
    if (ids.has(t.id)) t.id = uid();
    ids.add(t.id);
  }
  const activeRaw = typeof d.activeTaskId === 'string' ? d.activeTaskId : null;
  const activeTaskId = tasks.some((t) => t.id === activeRaw && !t.done) ? activeRaw : null;
  const history: AppData['history'] = {};
  if (isObj(d.history))
    for (const [k, v] of Object.entries(d.history)) {
      const day = isDayKey(k) ? sanitizeDay(v) : null;
      if (day) history[k] = day;
    }
  compact(history, now);
  const totals = isObj(d.totals) ? d.totals : {};
  return {
    v: 2,
    settings,
    run: sanitizeRun(d.run, settings, now),
    tasks,
    activeTaskId,
    side: (Array.isArray(d.side) ? d.side : [])
      .map((x) => sanitizeSide(x, now))
      .filter((s): s is SideTimer => !!s)
      .slice(0, MAX_SIDE_TIMERS),
    history,
    totals: {
      sessions: int(totals.sessions, 0, 0, 10_000_000),
      minutes: int(totals.minutes, 0, 0, 1_000_000_000),
    },
    streakCarry: sanitizeCarry(d.streakCarry),
  };
}

/** Converts MOD.01 (`pomodoro.mod1`) data into the v2 shape. */
export function migrateLegacy(raw: unknown, now: number, base: Settings = DEFAULT_SETTINGS): AppData {
  const d = isObj(raw) ? raw : {};
  const settings = sanitizeSettings({ ...base, ...(isObj(d.settings) ? d.settings : {}) }, base);
  const stats = isObj(d.stats) ? d.stats : {};
  const history: Record<string, unknown> = {};
  if (isDayKey(stats.date)) {
    const log = Array.isArray(stats.log)
      ? stats.log.filter(isObj).map((e) => ({ ts: e.ts, min: e.min, task: null, interruptions: 0 }))
      : [];
    history[stats.date] = { sessions: stats.sessions, minutes: stats.minutes, interruptions: 0, log };
  }
  const legacyRun = isObj(d.run) ? d.run : null;
  let run: unknown = null;
  if (legacyRun) {
    const phase = oneOf(legacyRun.phase, PHASES, 'focus');
    // MOD.01 did not record phase length; the current setting is the best available guess.
    run = { ...legacyRun, total: phaseMs(settings, phase), interruptions: 0 };
  }
  return sanitize(
    {
      settings,
      run,
      tasks: Array.isArray(d.tasks) ? d.tasks.map((t) => (isObj(t) ? { ...t, createdAt: now } : t)) : [],
      activeTaskId: d.activeTaskId,
      side: d.side,
      history,
      totals: { sessions: stats.allSessions, minutes: stats.allMinutes },
      streakCarry: isDayKey(stats.lastDay) ? { lastDay: stats.lastDay, streak: stats.streak } : null,
    },
    now,
    base,
  );
}

/** Loads from the stored v2 payload, else migrates the legacy payload, else starts fresh. */
export function loadData(
  v2Json: string | null,
  legacyJson: string | null,
  now: number,
  base: Settings = DEFAULT_SETTINGS,
): { data: AppData; source: 'v2' | 'legacy' | 'fresh' } {
  const parse = (s: string | null): unknown => {
    if (!s) return null;
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };
  const v2 = parse(v2Json);
  if (isObj(v2)) return { data: sanitize(v2, now, base), source: 'v2' };
  const legacy = parse(legacyJson);
  if (isObj(legacy)) return { data: migrateLegacy(legacy, now, base), source: 'legacy' };
  return { data: emptyData(base), source: 'fresh' };
}

export const serialize = (data: AppData): string => JSON.stringify(data);

/* ---------- backups ---------- */

export function makeBackup(data: AppData, now: number): string {
  return JSON.stringify(
    { app: BACKUP_APP_ID, version: 2, exportedAt: new Date(now).toISOString(), data },
    null,
    1,
  );
}

export class BackupError extends Error {}

/** Checked against the file size before reading, so a huge file is never loaded into memory. */
export function assertBackupSize(bytes: number) {
  if (bytes > MAX_BACKUP_BYTES) throw new BackupError('File is too large to be a backup.');
}

export function parseBackup(text: string, now: number): AppData {
  assertBackupSize(text.length);
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new BackupError('File is not valid JSON.');
  }
  if (!isObj(parsed)) throw new BackupError('Unrecognised backup format.');
  if (parsed.app === BACKUP_APP_ID && isObj(parsed.data)) return sanitize(parsed.data, now);
  // Accept a raw MOD.01 localStorage dump as well.
  if (isObj(parsed.settings) && isObj(parsed.stats)) return migrateLegacy(parsed, now);
  throw new BackupError('This file is not a Pomodoro backup.');
}

export const backupFileName = (now: number) => `pomodoro-backup-${dayKey(now)}.json`;

/* ---------- safe storage adapter ---------- */

export interface KV {
  get(key: string): string | null;
  set(key: string, value: string): boolean;
  remove(key: string): void;
  readonly persistent: boolean;
}

/** Wraps localStorage; falls back to memory when unavailable (private mode, disabled, quota). */
export function createKV(backing: Storage | null): KV {
  const mem = new Map<string, string>();
  // Keys whose last write failed: the backing copy is stale, so reads must come from memory.
  const stale = new Set<string>();
  let persistent = false;
  if (backing) {
    try {
      const probe = '__pomodoro_probe__';
      backing.setItem(probe, '1');
      backing.removeItem(probe);
      persistent = true;
    } catch {
      persistent = false;
    }
  }
  return {
    get persistent() {
      return persistent;
    },
    get(key) {
      if (persistent && !stale.has(key)) {
        try {
          return backing!.getItem(key);
        } catch {
          /* fall through */
        }
      }
      return mem.get(key) ?? null;
    },
    set(key, value) {
      mem.set(key, value);
      if (!persistent) return false;
      try {
        backing!.setItem(key, value);
        stale.delete(key);
        return true;
      } catch {
        stale.add(key);
        return false;
      }
    },
    remove(key) {
      mem.delete(key);
      stale.delete(key);
      try {
        backing?.removeItem(key);
      } catch {
        /* ignore */
      }
    },
  };
}
